import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../Authentication/AuthModal.css";
import LoginModal from "../../Authentication/LoginModal";
import RegisterModal from "../../Authentication/RegisterModal";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { LandingContext } from "../../Components/Profile/LandingContext";
import DefaultData from "../../CompanyData/DefaultData";
import ForgotPasswordModal from "../../Authentication/ForgotPasswordModal";

const navLinks = [
  { to: "/flight/search",      icon: "/assets/images/icons/flight.png",      label: "Flights",      iconSize: 26 },
  { to: "/hotel/search",       icon: "/assets/images/icons/hotel.png",        label: "Hotels",       iconSize: 26 },
  { to: "/holidays",           icon: "/assets/images/icons/holidays.png",     label: "Packages",     iconSize: 26 },
  { to: "/search/sightseeing", icon: "/assets/images/icons/sightseeing.png",  label: "Sightseeing",  iconSize: 22 },
  { to: "/umrahs",             icon: "/assets/images/icons/umrah.png",        label: "Umrah",        iconSize: 22 },
  { to: "/hajj",               icon: "/assets/images/icons/hajj.png",         label: "Hajj",         iconSize: 22 },
];

export default function PublicHeader() {
  const { user } = useAuth();
  const location = useLocation();
  const landingData = useContext(LandingContext);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const closeAll = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setShowForgotPasswordModal(false);
  };

  return (
    <>
      <header
        className={`baba-navbar${scrolled ? " baba-navbar--scrolled" : ""}`}
        style={{ "--brand-color": DefaultData.THEMECOLOR1, zIndex: 999 }}
      >
        <div className="baba-navbar__inner">
          {/* Logo */}
          <Link to="/" className="baba-navbar__logo">
            <img
              src="/assets/images/logo.png"
              alt="Baba Air"
              style={{ height: 46, width: "auto" }}
            />
          </Link>

          {/* Desktop nav links */}
          <nav className="baba-navbar__links">
            {navLinks.map(({ to, icon, label, iconSize }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`baba-navbar__link${active ? " active" : ""}`}
                >
                  <img src={icon} alt={label} width={iconSize} height={iconSize} className="baba-navbar__link-icon" />
                  <span>{label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right: account btn + hamburger */}
          <div className="baba-navbar__actions">
            {user ? (
              <Link to="/my-profile" className="baba-navbar__account-btn">
                <i className="ri-user-3-line"></i>
                <span>My Account</span>
              </Link>
            ) : (
              <button
                className="baba-navbar__account-btn"
                onClick={() => { closeAll(); setShowLoginModal(true); }}
              >
                <i className="ri-user-3-line"></i>
                <span>Login</span>
              </button>
            )}

            {/* Hamburger */}
            <button
              className={`baba-navbar__hamburger${menuOpen ? " open" : ""}`}
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <div className={`baba-navbar__drawer${menuOpen ? " open" : ""}`}>
          {navLinks.map(({ to, icon, label, iconSize }) => (
            <Link key={to} to={to} className="baba-navbar__drawer-link">
              <img src={icon} alt={label} width={iconSize} height={iconSize} />
              {label}
            </Link>
          ))}
          <div className="baba-navbar__drawer-divider" />
          {user ? (
            <Link to="/my-profile" className="baba-navbar__drawer-account">
              <i className="ri-user-3-line" /> My Account
            </Link>
          ) : (
            <button
              className="baba-navbar__drawer-account"
              onClick={() => { setMenuOpen(false); closeAll(); setShowLoginModal(true); }}
            >
              <i className="ri-user-3-line" /> Login
            </button>
          )}
        </div>
      </header>

      {/* No spacer needed — sticky positioning */}

      {/* Modals */}
      {!user && (
        <>
          <LoginModal
            show={showLoginModal}
            onClose={closeAll}
            onSwitchToRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }}
            onSwitchToForgotPassword={() => { setShowLoginModal(false); setShowForgotPasswordModal(true); }}
          />
          <RegisterModal
            show={showRegisterModal}
            onClose={closeAll}
            onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }}
            onLoginSuccess={closeAll}
          />
          <ForgotPasswordModal
            show={showForgotPasswordModal}
            onClose={closeAll}
            onSwitchToLogin={() => { setShowForgotPasswordModal(false); setShowLoginModal(true); }}
          />
        </>
      )}

      <style>{`
        .baba-navbar {
          position: sticky;
          top: 0;
          z-index: 999;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(226,232,240,0.8);
          transition: box-shadow 0.3s ease, background 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        .baba-navbar--scrolled {
          box-shadow: 0 4px 24px rgba(15,28,45,0.10);
          background: rgba(255,255,255,0.99);
        }
        .baba-navbar__inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          height: 66px;
          gap: 1rem;
        }
        .baba-navbar__logo { display: flex; align-items: center; flex-shrink: 0; }
        .baba-navbar__links {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex: 1;
          justify-content: center;
        }
        .baba-navbar__link {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0.45rem 0.85rem;
          border-radius: 8px;
          font-size: 0.88rem;
          font-weight: 500;
          color: #374151;
          transition: all 0.22s ease;
          white-space: nowrap;
          text-decoration: none;
        }
        .baba-navbar__link:hover {
          background: #f0f7ff;
          color: var(--brand-color, #2D93D3);
        }
        .baba-navbar__link.active {
          background: linear-gradient(135deg, rgba(45,147,211,0.12), rgba(200,168,75,0.08));
          color: var(--brand-color, #2D93D3);
          font-weight: 600;
        }
        .baba-navbar__link-icon { object-fit: contain; }
        .baba-navbar__actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-shrink: 0;
        }
        .baba-navbar__account-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--brand-color, #2D93D3);
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 0.48rem 1.15rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.22s ease;
          text-decoration: none;
          font-family: 'Inter', sans-serif;
          white-space: nowrap;
        }
        .baba-navbar__account-btn:hover {
          background: #1a7ab8;
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(45,147,211,0.35);
          color: #fff;
        }

        /* Hamburger */
        .baba-navbar__hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
        }
        .baba-navbar__hamburger span {
          display: block;
          width: 24px;
          height: 2px;
          background: #374151;
          border-radius: 2px;
          transition: all 0.3s ease;
          transform-origin: center;
        }
        .baba-navbar__hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .baba-navbar__hamburger.open span:nth-child(2) { opacity: 0; }
        .baba-navbar__hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        /* Mobile drawer */
        .baba-navbar__drawer {
          display: none;
          flex-direction: column;
          background: #fff;
          border-top: 1px solid #E2E8F0;
          padding: 0.75rem 1rem 1.25rem;
          gap: 0.25rem;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.35s ease;
        }
        .baba-navbar__drawer.open {
          max-height: 480px;
        }
        .baba-navbar__drawer-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 0.65rem 0.75rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          color: #374151;
          text-decoration: none;
          transition: background 0.2s;
        }
        .baba-navbar__drawer-link:hover { background: #f0f7ff; color: #2D93D3; }
        .baba-navbar__drawer-divider { border-top: 1px solid #E2E8F0; margin: 0.5rem 0; }
        .baba-navbar__drawer-account {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #2D93D3;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 0.65rem 1rem;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          font-family: 'Inter', sans-serif;
          width: 100%;
          justify-content: center;
        }

        @media (max-width: 992px) {
          .baba-navbar__links { display: none; }
          .baba-navbar__hamburger { display: flex; }
          .baba-navbar__drawer { display: flex; }
        }
        @media (max-width: 480px) {
          .baba-navbar__account-btn span { display: none; }
          .baba-navbar__account-btn { padding: 0.48rem 0.75rem; }
        }
      `}</style>
    </>
  );
}
